import { useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { PageLoading } from '@/components/common/PageStates'
import { DocumentWizardShell } from '@/components/rfid/DocumentWizardShell'
import { LineItemCard } from '@/components/rfid/LineItemCard'
import { WizardNavFooter } from '@/components/rfid/WizardNavFooter'
import { BRAND, DOC_BLUSH } from '@/constants/brand'
import { getMovement } from '@/config/movements'
import { useStoreHydration } from '@/hooks/useStoreHydration'
import { rfidService } from '@/services/rfid.service'
import { useAuthStore } from '@/stores/authStore'
import { useDocumentWizardStore } from '@/stores/documentWizardStore'
import { getEmployeeCode } from '@/utils/employeeCode'
import { buildPostingPreview } from '@/utils/postingPreview'
import { getKitWiseReviewSections, getReviewLineProgress } from '@/utils/scanStats'
import { allScannableComplete } from '@/utils/tagAllocation'
import { buildReferenceNumber } from '@/utils/successSummary'
import { saveRecentDocumentScan } from '@/utils/recentScans'

export default function WizardConfirmPage() {
  const navigate = useNavigate()
  const hydrated = useStoreHydration(useDocumentWizardStore.persist)
  const user = useAuthStore((s) => s.user)
  const employeeCode = getEmployeeCode(user)

  const movementId = useDocumentWizardStore((s) => s.movementId)
  const setSubmitResult = useDocumentWizardStore((s) => s.setSubmitResult)
  const document = useDocumentWizardStore((s) => s.document)
  const variant = useDocumentWizardStore((s) => s.variant)
  const lines = useDocumentWizardStore((s) => s.lines)
  const kitGroups = useDocumentWizardStore((s) => s.kitGroups)
  const kitBom = useDocumentWizardStore((s) => s.kitBom)
  const tagSet = useDocumentWizardStore((s) => s.tagSet)
  const transport = useDocumentWizardStore((s) => s.transport)
  const effectiveDate = useDocumentWizardStore((s) => s.effectiveDate)
  const remarks = useDocumentWizardStore((s) => s.remarks)
  const setRemarks = useDocumentWizardStore((s) => s.setRemarks)
  const transferBpCode = useDocumentWizardStore((s) => s.transferBpCode)
  const transferCustomerName = useDocumentWizardStore((s) => s.transferCustomerName)
  const transferToBpCode = useDocumentWizardStore((s) => s.transferToBpCode)
  const transferToCustomerName = useDocumentWizardStore((s) => s.transferToCustomerName)
  const transferDetails = useDocumentWizardStore((s) => s.transferDetails)
  const transferLineOfBusiness = useDocumentWizardStore((s) => s.transferLineOfBusiness)
  const transferBaseDocNo = useDocumentWizardStore((s) => s.transferBaseDocNo)
  const useAutoTransferType = useDocumentWizardStore((s) => s.useAutoTransferType)
  const customerLob = useDocumentWizardStore((s) => s.customerLob)
  const customerLobText = useDocumentWizardStore((s) => s.customerLobText)
  const customerAccountGroup = useDocumentWizardStore((s) => s.customerAccountGroup)
  const customerPostingCustomerCode = useDocumentWizardStore((s) => s.customerPostingCustomerCode)
  const customerDetailsError = useDocumentWizardStore((s) => s.customerDetailsError)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const kitSections = useMemo(
    () =>
      variant === 'kit' && kitGroups.length > 0
        ? getKitWiseReviewSections(kitGroups, kitBom)
        : null,
    [variant, kitGroups, kitBom]
  )

  const lineProgress = useMemo(
    () => getReviewLineProgress(lines, kitGroups, kitBom, tagSet),
    [lines, kitGroups, kitBom, tagSet]
  )

  const allScansComplete = useMemo(
    () => allScannableComplete(lines, kitGroups, kitBom),
    [lines, kitGroups, kitBom]
  )

  if (!hydrated) {
    return <PageLoading tip="Restoring scan session…" />
  }

  if (!document || !movementId) {
    return <Navigate to="/" replace />
  }

  const movement = getMovement(movementId)
  const postingPreview = buildPostingPreview(movement, useAutoTransferType)
  const postingBlocked =
    postingPreview.requiresCustomerDetails && Boolean(customerDetailsError)

  const onSubmit = async () => {
    if (postingBlocked) {
      toast.error(customerDetailsError ?? 'Customer details required before post')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const result = await rfidService.postDocument(movement, {
        userId: employeeCode,
        document,
        effectiveDate,
        transport,
        lines,
        kitGroups,
        kitBom,
        remarks,
        variant,
        transferBpCode: transferBpCode || undefined,
        transferToBpCode: transferToBpCode || undefined,
        transferToCustomerName: transferToCustomerName || undefined,
        transferCustomerName: transferCustomerName || undefined,
        transferDetails,
        transferLineOfBusiness: transferLineOfBusiness || undefined,
        transferBaseDocNo: transferBaseDocNo || undefined,
        useAutoTransferType,
      })
      if (!result.success) {
        setError(result.message)
        toast.error(result.message)
        return
      }
      setSubmitResult(result.message, result.raw)
      saveRecentDocumentScan({ status: 'posted', sapMessage: result.message })
      navigate('/wizard/success', {
        state: {
          message: result.message,
          referenceNumber: buildReferenceNumber(
            movementId,
            String(document.DocumentNo ?? ''),
            result.message
          ),
        },
      })
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string'
          ? String((e as { message: string }).message)
          : e instanceof Error
            ? e.message
            : 'Submit failed'
      setError(msg)
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <DocumentWizardShell
      step={3}
      title={`${movement.legacyName} — Confirm`}
      subtitle="Step 3 of 4 · Review and post"
      onBack={() => navigate('/wizard/scan')}
      document={document}
      hideDocumentSummary
      footer={
        <WizardNavFooter
          onBack={() => navigate('/wizard/scan')}
          onNext={() => void onSubmit()}
          nextLabel="Post document"
          nextDisabled={postingBlocked}
          nextLoading={loading}
        />
      }
    >
      <div
        className="rfid-confirm-card"
        style={{
          backgroundColor: postingBlocked ? 'rgba(239,68,68,0.08)' : DOC_BLUSH,
          borderColor: postingBlocked ? '#EF4444' : `${BRAND}44`,
        }}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 truncate text-[16px] font-black text-foreground">
            #{String(document.DocumentNo ?? '—')}
          </div>
          <span
            className="shrink-0 rounded-md bg-white px-2 py-0.5 text-[10px] font-extrabold"
            style={{ color: BRAND }}
          >
            {effectiveDate.toLocaleDateString('en-GB')}
          </span>
        </div>

        <div className="mb-2 text-[12px] font-semibold leading-4 text-muted-foreground">
          {String(document.CustName ?? '—')}
        </div>

        <div className="mb-2 h-px" style={{ backgroundColor: `${BRAND}22` }} />

        <div className="mb-2 flex gap-2.5">
          <span className="w-24 shrink-0 pt-0.5 text-[10px] font-extrabold uppercase text-muted-foreground">
            Posting
          </span>
          <span className="min-w-0 flex-1 text-[13px] font-extrabold" style={{ color: BRAND }}>
            {postingPreview.modeLabel} · {postingPreview.sapValue}
          </span>
        </div>

        <div className="mb-2 flex gap-2.5">
          <span className="w-24 shrink-0 pt-0.5 text-[10px] font-extrabold uppercase text-muted-foreground">
            Customer
          </span>
          <span className="min-w-0 flex-1 text-[13px] font-extrabold text-foreground">
            {customerPostingCustomerCode || '—'}
          </span>
        </div>

        <div className="mb-0 flex gap-2.5">
          <span className="w-24 shrink-0 pt-0.5 text-[10px] font-extrabold uppercase text-muted-foreground">
            LOB / AG
          </span>
          <span className="min-w-0 flex-1 text-[13px] font-extrabold text-foreground">
            {[customerLob || customerLobText, customerAccountGroup].filter(Boolean).join(' · ') ||
              '—'}
          </span>
        </div>

        {transferBpCode ? (
          <div className="mt-2 flex gap-2.5">
            <span className="w-24 shrink-0 pt-0.5 text-[10px] font-extrabold uppercase text-muted-foreground">
              From
            </span>
            <span className="min-w-0 flex-1 text-[13px] font-extrabold text-foreground">
              {transferCustomerName} ({transferBpCode})
            </span>
          </div>
        ) : null}

        {transferToBpCode ? (
          <div className="mt-2 flex gap-2.5">
            <span className="w-24 shrink-0 pt-0.5 text-[10px] font-extrabold uppercase text-muted-foreground">
              To
            </span>
            <span className="min-w-0 flex-1 text-[13px] font-extrabold text-foreground">
              {transferToCustomerName} ({transferToBpCode})
            </span>
          </div>
        ) : null}

        {transferBaseDocNo ? (
          <div className="mt-2 flex gap-2.5">
            <span className="w-24 shrink-0 pt-0.5 text-[10px] font-extrabold uppercase text-muted-foreground">
              Base doc
            </span>
            <span className="min-w-0 flex-1 text-[13px] font-extrabold text-foreground">
              {transferBaseDocNo}
            </span>
          </div>
        ) : null}

        {postingBlocked ? (
          <div className="mt-3 rounded-[10px] border border-red-400 bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-700">
            {customerDetailsError}
          </div>
        ) : null}
      </div>

      {movement.wizardKind === 'standard' || movement.wizardKind === 'relocation' ? (
        <div className="mb-3 rounded-[14px] border border-border bg-card p-3.5">
          <div className="rfid-section-label mb-2">Transport</div>
          <div className="grid grid-cols-2 gap-2 text-[12px]">
            <div>
              <div className="font-extrabold uppercase text-muted-foreground text-[10px]">Vehicle</div>
              <div className="font-bold">{transport.VEHICLE_NO || '—'}</div>
            </div>
            <div>
              <div className="font-extrabold uppercase text-muted-foreground text-[10px]">Driver</div>
              <div className="font-bold">{transport.DRIVER_NAME || '—'}</div>
            </div>
            <div>
              <div className="font-extrabold uppercase text-muted-foreground text-[10px]">Mobile</div>
              <div className="font-bold">{transport.DRIVER_MOB_NO || '—'}</div>
            </div>
            <div>
              <div className="font-extrabold uppercase text-muted-foreground text-[10px]">LR</div>
              <div className="font-bold">{transport.LR_NO || '—'}</div>
            </div>
            <div>
              <div className="font-extrabold uppercase text-muted-foreground text-[10px]">Mode</div>
              <div className="font-bold">{transport.TRANSPORT_MODE || '—'}</div>
            </div>
            <div>
              <div className="font-extrabold uppercase text-muted-foreground text-[10px]">Transporter</div>
              <div className="font-bold">{transport.TRANSPORTER_CODE || '—'}</div>
            </div>
          </div>
        </div>
      ) : null}

      {kitSections ? (
        kitSections.map((section) => (
          <div key={section.kitIndex} className="mb-3">
            <div className="rfid-section-label">
              Kit {section.kitMaterial || section.kitIndex + 1}
              {section.kitDesc ? ` · ${section.kitDesc}` : ''}
            </div>
            <div className="rfid-line-grid">
              {section.components.map((r) => (
                <LineItemCard
                  key={r.line.id}
                  materialCode={r.line.materialCode}
                  materialDesc={r.line.materialDesc}
                  requiredQty={r.required}
                  scanQty={r.scanned}
                  nonScanQty={r.nonScan}
                  status={r.status}
                  scannable
                  tags={r.line.rfidCodes}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="mb-3">
          <div className="rfid-section-label">Lines</div>
          <div className="rfid-line-grid">
            {lineProgress.map((r) => (
              <LineItemCard
                key={r.line.id}
                materialCode={r.line.materialCode}
                materialDesc={r.line.materialDesc}
                requiredQty={r.required}
                scanQty={r.scanned}
                nonScanQty={r.nonScan}
                status={r.status}
                scannable
                tags={r.line.rfidCodes}
              />
            ))}
          </div>
        </div>
      )}

      <div className="rfid-field">
        <label className="rfid-field__label" htmlFor="confirm-remarks">
          Remarks
        </label>
        <textarea
          id="confirm-remarks"
          rows={3}
          className="rfid-field__control"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Optional remarks for SAP"
        />
      </div>

      {!allScansComplete ? (
        <p className="mt-2 text-center text-[11.5px] font-bold text-amber-600">
          Some scannable lines are incomplete. You can still post if your process allows it.
        </p>
      ) : null}

      {error ? (
        <p className="mt-2 text-center text-[12px] font-bold text-red-500">{error}</p>
      ) : null}
    </DocumentWizardShell>
  )
}
